/* eslint-disable max-len */
/* eslint-disable security/detect-object-injection */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import CardHeader from '../../../components/CardHeader';
import ItemShimmer from '../../../components/Shimmer/itemShimmer';
import Pagination from '../../../components/Pagination';
import EmptyAddScreen from '../../../components/EmptyAddScreen';
import ConfirmationPrompt from '../../../components/ConfirmationPrompt';
import AnimatedModel from '../../../components/Animation/AnimatedModel';
import Toast from '../../../components/Toast';
import FeeStructureDropdown from '../../../components/FeeStructureDropdown';
import Loader from '../../../components/Loader';
import Search from '../../StudentMaster/FeeCollection/Components/Search';
import GlobalContext from '../../../components/Context/GloablContext';
import { dataService } from '../../../services/data.services';
import { GetBranchId } from '../../../CommonMethods/GetBranchId';
import { BroadcastsList } from './BroadcastsSlice';
import BroadcastsTable from './BroadcastsTable';
import { fetchNoticeTypeOptions, getNoticeTypeLabel } from '../Classroom/noticeTypeUtils';
import { isAcademicYearCompleted } from '../../HolidayCalendar/CommonMethods';

export default function Broadcasts() {
    const { isSideNavBar, setToastmessage, isInactiveBranch } = useContext(GlobalContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams({ page_number: 1 });

    const [list, setList] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingFilterAYs, setLoadingFilterAYs] = useState(true);
    const [loadingActivation, setLoadingActivation] = useState(false);
    const [applyingFilters, setApplyingFilters] = useState(false);
    const [firstLoading, setFirstLoading] = useState(false);
    const [emptyScreenMessage, setEmptyScreenMessage] = useState('');
    const [confirmationState, setConfirmationState] = useState({});
    const [toastErrorMessage, setToastErrorMessage] = useState('');

    const [filterAYs, setFilterAYs] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [filterAY, setFilterAY] = useState({});
    const [filterType, setFilterType] = useState('');
    const [filterSearch, setFilterSearch] = useState('');
    const [loadingTypeOptions, setLoadingTypeOptions] = useState(true);

    const today = moment().format('YYYY-MM-DD');
    const defaultAcademicYear = useMemo(
        () => filterAYs.find((item) => String(item?.status || '').toLowerCase() === 'ongoing') || filterAYs[0] || null,
        [filterAYs]
    );
    const academicYearOptions = useMemo(() => filterAYs.map((item) => ({ ...item })), [filterAYs]);
    const typeDropdownOptions = useMemo(
        () => [{ value: '', label: 'All Types', name: 'All Types' }, ...typeOptions.map((item) => ({ ...item, name: item.label }))],
        [typeOptions]
    );

    const fetchAcademicYears = useCallback(async () => {
        setLoadingFilterAYs(true);
        try {
            const branch_id = await GetBranchId();
            const res = await dataService.GetAPI(`academic-years?branch_id=${branch_id}&filter=true&status=ongoing,upcoming,completed`);
            setFilterAYs(res.data?.data?.data || []);
            setTypeOptions(await fetchNoticeTypeOptions(branch_id, 'broadcasts'));
        } catch {
            setToastErrorMessage('Something went wrong while loading filters');
        } finally {
            setLoadingFilterAYs(false);
            setLoadingTypeOptions(false);
        }
    }, []);

    const fetchList = useCallback(async () => {
        try {
            setLoading(true);
            const branch_id = await GetBranchId();
            const queryString = new URLSearchParams(Object.fromEntries(searchParams)).toString();
            const endpoint = `notice-board/broadcasts?branch_id=${branch_id}&${queryString}`;
            dispatch(BroadcastsList(endpoint)).then((response) => {
                const data = response?.payload?.data;
                console.log('Fetched Broadcasts:', data);
                if (data?.status === 200) {
                    const listData = data.data || {};
                    const rows = listData.data || [];
                    setList(listData);
                    const selectedAcademicYearId = searchParams.get('academic_year_id') || '';
                    const isDefaultAcademicYearOnly = selectedAcademicYearId && selectedAcademicYearId === defaultAcademicYear?._id;
                    if (rows.length === 0 && (listData.total_count || 0) === 0 && !searchParams.get('type') && !searchParams.get('search') && (isDefaultAcademicYearOnly || !selectedAcademicYearId)) {
                        setFirstLoading(true);
                    } else {
                        setFirstLoading(false);
                        setEmptyScreenMessage('No matching records found.');
                    }
                } else {
                    setEmptyScreenMessage('No data found.');
                }
                setLoading(false);
                setApplyingFilters(false);
            });
        } catch {
            setLoading(false);
            setEmptyScreenMessage('No data found.');
            setApplyingFilters(false);
        }
    }, [defaultAcademicYear?._id, dispatch, searchParams]);

    useEffect(() => { fetchAcademicYears(); }, []);
    useEffect(() => {
        if (loadingFilterAYs) return;
        const academicYearId = searchParams.get('academic_year_id') || '';
        if (!academicYearId && defaultAcademicYear?._id) {
            const params = Object.fromEntries(searchParams);
            setSearchParams({
                ...params,
                academic_year_id: defaultAcademicYear._id,
                page_number: params.page_number || 1,
            });
            return;
        }
        fetchList();
    }, [defaultAcademicYear, fetchList, loadingFilterAYs, searchParams, setSearchParams]);

    useEffect(() => {
        const academicYearId = searchParams.get('academic_year_id') || defaultAcademicYear?._id || '';
        const selectedAY = filterAYs.find((item) => item._id === academicYearId) || defaultAcademicYear || {};
        setFilterAY(selectedAY);
        setFilterType(searchParams.get('type') || '');
        setFilterSearch(searchParams.get('search') || '');
    }, [defaultAcademicYear, filterAYs, searchParams]);

    const handleFetch = () => {
        const params = { page_number: 1 };
        if (filterAY?._id || defaultAcademicYear?._id) params.academic_year_id = filterAY?._id || defaultAcademicYear._id;
        if (filterType) params.type = filterType;
        if (filterSearch.trim()) params.search = filterSearch.trim();
        const nextSearchParams = new URLSearchParams(params).toString();
        if (nextSearchParams === searchParams.toString()) {
            fetchList();
            return;
        }
        setApplyingFilters(true);
        setSearchParams(params);
    };

    const handleSearch = useCallback((field, value) => {
        const nextSearch = String(value || '').trim();
        setFilterSearch(nextSearch);
        const params = { page_number: 1 };
        if (filterAY?._id || defaultAcademicYear?._id) params.academic_year_id = filterAY?._id || defaultAcademicYear._id;
        if (filterType) params.type = filterType;
        if (nextSearch) params.search = nextSearch;
        const nextSearchParams = new URLSearchParams(params).toString();
        if (nextSearchParams === searchParams.toString()) {
            fetchList();
            return;
        }
        setApplyingFilters(true);
        setSearchParams(params);
    }, [defaultAcademicYear?._id, fetchList, filterAY?._id, filterType, searchParams]);

    const buildBroadcastListUrl = useCallback((params = {}) => {
        const nextParams = new URLSearchParams();
        const academicYearId = params.academic_year_id || filterAY?._id || defaultAcademicYear?._id || '';
        if (academicYearId) nextParams.set('academic_year_id', academicYearId);
        if (params.type || filterType) nextParams.set('type', params.type || filterType);
        if (params.search || filterSearch.trim()) nextParams.set('search', params.search || filterSearch.trim());
        nextParams.set('page_number', String(params.page_number || searchParams.get('page_number') || 1));
        return `/noticeboard/broadcasts?${nextParams.toString()}`;
    }, [defaultAcademicYear?._id, filterAY?._id, filterSearch, filterType, searchParams]);

    const handleNavigateToAdd = () => {
        if (isAcademicYearCompleted(filterAY?._id ? filterAY : defaultAcademicYear)) {
            setToastErrorMessage('AY Completed, Broadcast cannot be added');
            return;
        }

        const params = new URLSearchParams();
        const selectedAcademicYear = filterAY?._id ? filterAY : defaultAcademicYear || {};
        if (selectedAcademicYear?._id) params.set('academic_year_id', selectedAcademicYear._id);
        if (filterType) params.set('type', filterType);
        if (filterSearch.trim()) params.set('search', filterSearch.trim());

        navigate(`/noticeboard/broadcasts/add${params.toString() ? `?${params.toString()}` : ''}`, {
            state: {
                broadcastContext: {
                    academicYear: selectedAcademicYear,
                    academicYears: filterAYs,
                    type: filterType,
                    typeOptions,
                    search: filterSearch.trim(),
                    listUrl: buildBroadcastListUrl(),
                },
            },
        });
    };

    const handleDelete = async () => {
        setLoadingActivation(true);
        try {
            const branch_id = await GetBranchId();
            const response = await dataService.DeleteAPI(`notice-board/broadcasts/${confirmationState.item._id}?branch_id=${branch_id}`);
            setLoadingActivation(false);
            setConfirmationState({});
            if (response?.data?.status === 200) {
                setToastmessage(response.data.message || 'Broadcast deleted successfully');
                fetchList();
            } else {
                setToastErrorMessage(response?.data?.message || 'Something went wrong');
            }
        } catch (error) {
            setLoadingActivation(false);
            setConfirmationState({});
            setToastErrorMessage(error?.response?.data?.message || 'Something went wrong');
        }
    };

    const canEdit = (item) => item.date >= today;
    const canDelete = (item) => item.date >= today;
    const selectedAcademicYear = filterAY?._id ? filterAY : defaultAcademicYear || {};
    const isAddBlocked = isAcademicYearCompleted(selectedAcademicYear);

    const handleOnClickPageNumber = (pageNum) => {
        const params = Object.fromEntries(searchParams);
        setSearchParams({ ...params, page_number: pageNum.selected + 1 });
    };

    return (
        <>
            <div className={`w-full main-screenInst overflow-x-hidden max-h-screen min-h-screen relative ${!isSideNavBar ? 'w-[270px]' : ''}`}>
                <div className={`mainDataCntnr ${!isSideNavBar ? 'normal' : 'wide'}`}>
                    <div className="mainDataCntnrTop shadow-s13">
                        <h5 className="pageHeader">Broadcasts</h5>
                        {toastErrorMessage !== '' && (
                            <Toast message={toastErrorMessage} type="error" setToastmessage={setToastErrorMessage} />
                        )}
                        {!isInactiveBranch && (
                            <div class="flex items-center">
                                <button
                                    type="button"
                                    className={`prmy-btn w-full ${isAddBlocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    onClick={handleNavigateToAdd}
                                    aria-disabled={isAddBlocked}
                                >
                                    Add Broadcast
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="mainDataCntnrListDiv scrollBlue_thick">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end border p-2 rounded sticky top-0 z-20 bg-white">
                            <div>
                                <p className=" font-normal text-[#031624] mb-1">Academic Year</p>
                                <FeeStructureDropdown
                                    dropDownName="academic_year"
                                    value={filterAY?.name ? `${filterAY.name} (${filterAY.status || ''})` : ''}
                                    options={academicYearOptions}
                                    placeHolder="Select Academic Year"
                                    handleOnchangeInput={(_, option) => setFilterAY(option)}
                                    loading={loadingFilterAYs}
                                />
                            </div>
                            <div>
                                <p className=" font-normal text-[#031624] mb-1">Type</p>
                                <FeeStructureDropdown
                                    dropDownName="type"
                                    value={getNoticeTypeLabel(filterType, typeOptions, 'All Types')}
                                    options={typeDropdownOptions}
                                    placeHolder="All Types"
                                    handleOnchangeInput={(_, option) => setFilterType(option?.value || '')}
                                    loading={loadingTypeOptions}
                                />
                            </div>
                            <div>
                                <p className=" font-normal text-[#031624] mb-1">Search</p>
                                <Search
                                    key={filterSearch}
                                    SearchValue={filterSearch}
                                    handleOnClickSearch={handleSearch}
                                    customSearchPlaceHolder="Title"
                                />
                            </div>
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleFetch}
                                    style={{ 'min-width': '120px' }}
                                    disabled={loadingFilterAYs || loadingTypeOptions || applyingFilters || loading}
                                    className="view-actn-btn w-full"
                                >
                                    {loadingFilterAYs || loadingTypeOptions || applyingFilters || loading ? <Loader colorCode="#ffffff" /> : 'Apply Filter'}
                                </button>
                            </div>
                        </div>

                        <div className="mt-4">
                            {loading ? (
                                <ItemShimmer />
                            ) : (
                                <>
                                    <BroadcastsTable
                                        data={list?.data || []}
                                        typeOptions={typeOptions}
                                        canEdit={canEdit}
                                        canDelete={canDelete}
                                        setConfirmationState={setConfirmationState}
                                        navigate={navigate}
                                        emptyScreenMessage={emptyScreenMessage}
                                        currentQuery={searchParams.toString()}
                                        broadcastContext={{
                                            academicYear: filterAY?._id ? filterAY : defaultAcademicYear || {},
                                            academicYears: filterAYs,
                                            type: filterType,
                                            typeOptions,
                                            search: filterSearch.trim(),
                                            listUrl: buildBroadcastListUrl(),
                                        }}
                                    />
                                    {list?.total_count > 10 && (
                                        <Pagination
                                            TotalCount={list.total_count}
                                            totalPageNumber={Math.ceil(list.total_count / 10)}
                                            selectedPageNumber={Number(searchParams.get('page_number')) || 1}
                                            handleOnClickPageNumber={handleOnClickPageNumber}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {Object.keys(confirmationState).length > 0 && (
                <AnimatedModel
                    Children={
                        <ConfirmationPrompt
                            handleOnCLickActivate={handleDelete}
                            handleClose={() => setConfirmationState({})}
                            tittle="Delete Broadcast"
                            message="Are you sure you want to delete this broadcast?"
                            buttonName="Delete"
                            loadingActivation={loadingActivation}
                        />
                    }
                />
            )}
        </>
    );
}
